import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import './App.scss'
import AppRoutes from './AppRoutes'
import Navbar from './shared/Navbar'
import Sidebar from './shared/Sidebar'
import Footer from './shared/Footer'
import admindata from './components/admindata'
import FirebaseMessaging from './services/FirebaseMessaging'
import { apiRequest, clearAuthAndRedirect } from './store/allApi'
import {
  ChatCallProvider,
  usePackageAuth,
  ChatWidget,
  E2eeProvider,
  usePushNotifications,
  useOriginSettings,
} from 'react-chat-call-socket'
import { chatConfig } from './chatConfig.js'

const FULL_PAGE_ROUTES = new Set([
  '/adminlogin',
  '/login',
  '/register',
  '/user-pages/login-1',
  '/user-pages/login-2',
  '/user-pages/register-1',
  '/user-pages/register-2',
  '/user-pages/lockscreen',
  '/error-pages/error-404',
  '/error-pages/error-500',
])

const ADMIN_PERMISSIONS = (data) => ({
  usertype: 'Admin',
  userAname: data.Name,
  dashboard: true,
  earnings: true,
  allAdmins: true,
  newAdmin: true,
  allUsers: true,
  sitesettings: true,
  newUser: true,
  pendingKyc: true,
  completedKyc: true,
  rejectKyc: true,
  supportChat: true,
  pushNotification: true,
  updateLegalData: true,
  allChallenges: true,
  completedChallenges: true,
  conflictChallenges: true,
  cancelledChallenges: true,
  runningChallenges: true,
  newChallenge: true,
  penalty: true,
  bonus: true,
  depositHistory: true,
  bonusHistory: true,
  referHistory: true,
  withdrawlHistory: true,
  allWithdrawlRequests: true,
  allDepositRequests: true,
  pages: true,
  bonusReport: true,
  penaltyReport: true,
  withdrawalReport: true,
  depositReport: true,
  Gatewaysettings: true,
  AgentWallet: true,
  walletHistory: true,
})

const agentPermissions = (data) => {
  const p = data.Permissions || []
  return {
    usertype: 'Agent',
    userAname: data.Name,
    dashboard: p[0]?.Status,
    earnings: p[1]?.Status,
    allAdmins: p[2]?.Status,
    newAdmin: p[3]?.Status,
    allUsers: p[4]?.Status,
    newUser: p[5]?.Status,
    pendingKyc: p[6]?.Status,
    completedKyc: p[7]?.Status,
    rejectKyc: p[8]?.Status,
    allChallenges: p[9]?.Status,
    completedChallenges: p[10]?.Status,
    conflictChallenges: p[11]?.Status,
    cancelledChallenges: p[12]?.Status,
    runningChallenges: p[13]?.Status,
    newChallenge: p[14]?.Status,
    penalty: p[15]?.Status,
    bonus: p[16]?.Status,
    depositHistory: p[17]?.Status,
    withdrawlHistory: p[18]?.Status,
    allWithdrawlRequests: p[19]?.Status,
    allDepositRequests: p[20]?.Status,
    pages: p[21]?.Status,
    bonusHistory: p[22]?.Status,
    bonusReport: p[23]?.Status,
    withdrawalReport: p[24]?.Status,
    depositReport: p[25]?.Status,
    penaltyReport: p[26]?.Status,
    supportChat: p[27]?.Status,
    pushNotification: p[28]?.Status,
    updateLegalData: p[29]?.Status,
    Gatewaysettings: p[30]?.Status,
    AgentWallet: p[31]?.Status,
    walletHistory: p[32]?.Status,
    referHistory: p[33]?.Status,
  }
}

function NotificationSetup({ user }) {
  const { permission, supported, subscribed, enable } = usePushNotifications({
    username: user?.name || 'Guest',
    vapidPublicKey: chatConfig.vapidPublicKey,
    appPath: '/',
  })

  useEffect(() => {
    if (permission === 'granted' && !subscribed) {
      enable().catch(() => {})
    }
  }, [permission, subscribed, enable])

  if (!chatConfig.vapidPublicKey || !supported) return null
  return null
}

function ChatIntegration({ loggedInUser }) {
  const access_token = localStorage.getItem('token')
  const profile = useMemo(
    () => ({
      name: loggedInUser?.name,
      mobile: loggedInUser?.mobile,
      profile_pic: loggedInUser?.avatarUrl,
    }),
    [loggedInUser]
  )

  const { user, token } = usePackageAuth({
    apiUrl: chatConfig.apiUrl,
    apiKey: chatConfig.apiKey,
    appToken: access_token,
    userType: chatConfig.userType,
    profile,
  })

  const embedOrigin =
    typeof window !== 'undefined'
      ? `${window.location.protocol}//${window.location.host}`
      : ''

  const { settings: originSettings } = useOriginSettings({
    apiUrl: chatConfig.apiUrl,
    apiKey: chatConfig.apiKey,
    origin: user?.origin || embedOrigin,
  })

  const hideChatButton = Boolean(originSettings?.hideChatButton)
  const hideCallButton = Boolean(originSettings?.hideCallButton)
  const showLauncher = chatConfig.showLauncher !== false && !hideChatButton

  if (!access_token || !token) return null

  return (
    <ChatCallProvider url={chatConfig.socketUrl} token={token}>
      <E2eeProvider
        userId={user?._id}
        apiUrl={chatConfig.apiUrl}
        apiKey={chatConfig.apiKey}
        token={token}
        enabled={chatConfig.e2ee !== false}
      >
        <NotificationSetup user={user} />
        <ChatWidget
          room={chatConfig.defaultRoom}
          username={user?.name || 'Guest'}
          userId={user?._id}
          userType={user?.user_type || chatConfig.userType}
          canViewDev={Boolean(user?.can_view_dev)}
          showLauncher={showLauncher}
          hideCallButton={hideCallButton}
          launcherHorizontal={chatConfig.launcherHorizontal || 'right'}
          launcherVertical={chatConfig.launcherVertical || 'bottom'}
          launcherOffsetYPercent={chatConfig.launcherOffsetYPercent ?? 4}
          launcherOffsetXPercent={chatConfig.launcherOffsetXPercent ?? 3}
        />
      </E2eeProvider>
    </ChatCallProvider>
  )
}

async function updateFirebaseToken(fcmtoken) {
  if (window.location.pathname === '/adminlogin' || !fcmtoken) return

  const token = localStorage.getItem('token')
  if (!token) return

  try {
    await apiRequest(
      `updateToken?firebaseToken=${fcmtoken}`,
      'GET',
      null,
      { Authorization: `Bearer ${token}` }
    )
  } catch (error) {
    console.log(error)
  }
}

const App = ({ loggedInUser }) => {
  const location = useLocation()
  const [permissions, setPermissions] = useState({})
  const [message, setMessage] = useState('')
  const hasToken = Boolean(localStorage.getItem('token'))

  const isFullPageLayout = useMemo(() => {
    const { pathname } = location
    if (!hasToken) return true
    if (FULL_PAGE_ROUTES.has(pathname)) return true
    const first = pathname.split('/')[1]
    return first === 'landing' || first === 'viewgame1'
  }, [location, hasToken])

  const loadPermissions = useCallback(async () => {
    const token = localStorage.getItem('token')
    if (!token || location.pathname === '/adminlogin') return

    try {
      const res = await apiRequest('admin/me', 'GET', null, {
        Authorization: `Bearer ${token}`,
      })
      localStorage.setItem('id', res.data?._id)
      localStorage.setItem('user', res.data?.user_type)

      let permission = {}
      if (res.data.user_type === 'Admin') {
        permission = ADMIN_PERMISSIONS(res.data)
      } else if (res.data.user_type === 'Agent') {
        permission = agentPermissions(res.data)
      }

      const withProfile = { ...permission, profile: res.data }
      setPermissions(withProfile)
      admindata.user = withProfile
    } catch (err) {
      if (err.response?.status === 401) {
        clearAuthAndRedirect()
      }
    }
  }, [location.pathname])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])

  useEffect(() => {
    loadPermissions()

    ;(async () => {
      try {
        const tokenfirebase =
          await FirebaseMessaging.requestNotificationPermission()
        await updateFirebaseToken(tokenfirebase)
        FirebaseMessaging.onMessage((payload) => {
          setMessage(payload?.notification?.body || '')
        })
      } catch (e) {
        console.log(e)
      }
    })()
  }, [loadPermissions])

  if (isFullPageLayout) {
    return (
      <div className='container-scroller'>
        <div className='container-fluid page-body-wrapper full-page-wrapper'>
          <div className='main-panel w-100'>
            <div className='content-wrapper' style={{ backgroundColor: '#fff', padding: 0 }}>
              <AppRoutes />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='container-scroller'>
      <Sidebar {...permissions} />
      <div className='container-fluid page-body-wrapper'>
        <Navbar usertype={permissions.usertype} userAname={permissions.userAname} />
        <div className='main-panel'>
          <div className='content-wrapper' style={{ backgroundColor: '#fff' }}>
            {message ? <div className='alert alert-info'>{message}</div> : null}
            <AppRoutes />
          </div>
          <Footer />
          <ChatIntegration loggedInUser={loggedInUser || permissions.profile} />
        </div>
      </div>
    </div>
  )
}

export default App